// The two Jira instances a squad can point at, shared by the onboarding
// scaffolder field (JiraInstanceField) and the Manage tab configuration form so
// the labels and URLs cannot drift between them. A third copy of these URLs
// lives in the scaffolder template's `default:` block (YAML cannot import TS);
// keep it in sync by hand.
//
// Two URLs per instance because they serve different consumers:
//   - browseBaseUrl -> the clickable link shown in Backstage (the "Usage by
//     ticket" table). For SaaS this is the human site, NOT the api gateway.
//   - apiBaseUrl    -> JIRA_BASE_URL for the PANDA agent runtime. For SaaS this
//     is the Atlassian cloud api gateway, which differs from the browse site.
export type JiraInstance = 'SaaS' | 'On-Premise';

export const JIRA_PROJECT_KEY = /^[A-Z][A-Z0-9]+$/;

export type JiraInstanceUrls = {
  apiBaseUrl: string;
  browseBaseUrl: string;
};

export const JIRA_PRESETS: Record<JiraInstance, JiraInstanceUrls> = {
  SaaS: {
    apiBaseUrl:
      'https://api.atlassian.com/ex/jira/23cadbe4-5ee9-46f7-8a55-f7ac9c44041a',
    browseBaseUrl: 'https://tdf.atlassian.net/browse/',
  },
  'On-Premise': {
    apiBaseUrl: 'https://jira.totalenergies.com/rest/api/2',
    browseBaseUrl: 'https://jira.totalenergies.com/browse/',
  },
};

export const JIRA_INSTANCES = Object.keys(JIRA_PRESETS) as JiraInstance[];

export const DEFAULT_JIRA_INSTANCE: JiraInstance = 'SaaS';

const LISTED_JIRA_INSTANCE: JiraInstance = 'SaaS';

export const isListedJiraInstance = (instance: JiraInstance): boolean =>
  instance === LISTED_JIRA_INSTANCE;

export const BOARD_LISTING_UNAVAILABLE =
  'Boards are listed on the SaaS Jira only. For On-Premise, enter the saved-filter ids by hand.';

export const PROJECT_LISTING_UNAVAILABLE =
  'Projects are listed on the SaaS Jira only. For On-Premise, type the project key.';

// The value carried by the scaffolder field and the Manage draft: the chosen
// instance plus the two URLs, which stay editable after a preset fills them.
export type JiraInstanceValue = {
  instance: JiraInstance;
  apiBaseUrl: string;
  browseBaseUrl: string;
};

export const jiraPresetValue = (instance: JiraInstance): JiraInstanceValue => ({
  instance,
  ...JIRA_PRESETS[instance],
});

// SaaS browse base, applied to every squad that stores no value so the
// pre-existing SaaS squads render working links with no migration. The browse
// base is now a key of the modifiable-attributes secret (BACKSTAGE_JIRA_BROWSE_BASE_URL),
// written by the Manage tab; it is no longer a catalog annotation.
export const DEFAULT_JIRA_BROWSE_BASE = 'https://tdf.atlassian.net/browse/';

// Base used to link a ticket key to Jira, from the stored
// BACKSTAGE_JIRA_BROWSE_BASE_URL attribute. An absent value is not "no value"
// but the SaaS default, so squads with nothing stored keep working links. A
// trailing slash is enforced so callers can safely concatenate base + ticketKey.
export const jiraBrowseBase = (stored?: string): string => {
  const base = stored?.trim() || DEFAULT_JIRA_BROWSE_BASE;
  return base.endsWith('/') ? base : `${base}/`;
};

// Project dashboard for a squad, derived from the browse base so it follows the
// SaaS/On-Premise choice. The browse base ends in `browse/`; the project page
// is its sibling `projects/<KEY>` (e.g. https://tdf.atlassian.net/projects/AGFAC),
// valid on both Jira Cloud and Server/DC. Replacing only the final `browse/`
// segment preserves any path prefix an on-premise instance may carry.
export const jiraProjectUrl = (
  browseBase: string,
  projectKey: string,
): string => {
  const base = browseBase.endsWith('/') ? browseBase : `${browseBase}/`;
  return `${base.replace(/browse\/$/, 'projects/')}${projectKey}`;
};
